import React from "react"
import { Card, CardBody, Input, FormFeedback } from "reactstrap"

function FoodPricing({
  dietaryPreferences = [],
  audienceTicketData,
  validation,
}) {
  if (!validation?.values) return null

  const earlyBirdEnabled = audienceTicketData?.ticketBooking?.earlyBird || false

  // Helper to safely get nested values from Formik objects (errors/touched)
  const getNestedValue = (obj, path) => {
    return path.split('.').reduce((acc, part) => acc && acc[part], obj);
  };

  const categories = []
  if (audienceTicketData?.ageGroup?.adult) {
    if (audienceTicketData?.audienceType?.member) categories.push("Adult Member")
    if (audienceTicketData?.audienceType?.nonMember) categories.push("Adult Non Member")
  }
  if (audienceTicketData?.ageGroup?.child) {
    if (audienceTicketData?.audienceType?.member) categories.push("Child Member")
    if (audienceTicketData?.audienceType?.nonMember) categories.push("Child Non Member")
  }

  if (!dietaryPreferences.length) {
    return (
      <Card className="shadow-sm border-0">
        <CardBody className="p-4 text-center">
          <i className="mdi mdi-food-off font-size-48 text-muted d-block mb-2"></i>
          <h6 className="text-muted">No Food Category Selected</h6>
        </CardBody>
      </Card>
    )
  }

  return (
    <>
      <div className="d-flex align-items-center mb-4">
        <div className="avatar-sm me-3">
          <span className="avatar-title rounded-circle bg-success bg-soft text-success font-size-18">
            <i className="mdi mdi-food"></i>
          </span>
        </div>
        <div>
          <h5 className="mb-0 fw-semibold">Food Pricing</h5>
          <small className="text-muted">Set pricing per food type and audience category</small>
        </div>
      </div>

      <div className="d-flex flex-column gap-4">
        {dietaryPreferences.map(pref => {
          const prefKey = pref.toLowerCase().replace(/-/g, "_")
          const badgeColor = pref === "Veg" ? "success" : pref === "Non-Veg" ? "danger" : "info"

          return (
            <div key={pref}>
              <div className="d-flex align-items-center mb-3">
                <span className={`badge bg-${badgeColor} me-2`}>{pref}</span>
                <h6 className="mb-0 fw-semibold text-capitalize">Pricing</h6>
              </div>
              <div className="table-responsive">
                <table className="table table-bordered table-hover align-middle mb-0">
                  <thead className="table-light">
                    <tr>
                      <th className="fw-semibold">Category</th>
                      {earlyBirdEnabled && (
                        <th className="fw-semibold text-warning">
                          <i className="mdi mdi-bird me-1"></i>Early Bird Cost
                        </th>
                      )}
                      <th className="fw-semibold text-primary">Standard Cost</th>
                    </tr>
                  </thead>
                  <tbody>
                    {categories.length === 0 ? (
                      <tr>
                        <td colSpan={earlyBirdEnabled ? 3 : 2} className="text-center text-muted py-3">
                          No audience selected
                        </td>
                      </tr>
                    ) : (
                      categories.map(cat => {
                        const catKey = cat.toLowerCase().replace(/ /g, "_")
                        const earlyName = `foodPricing.${prefKey}.${catKey}.early`
                        const standardName = `foodPricing.${prefKey}.${catKey}.standard`

                        // Logic to determine if error should be shown
                        const standardError = getNestedValue(validation.errors, standardName)
                        const earlyError = getNestedValue(validation.errors, earlyName)
                        
                        const isStandardTouched = getNestedValue(validation.touched, standardName)
                        const isEarlyTouched = getNestedValue(validation.touched, earlyName)

                        const showStandardError = !!(standardError && (isStandardTouched || validation.submitCount > 0))
                        const showEarlyError = !!(earlyError && (isEarlyTouched || validation.submitCount > 0))

                        return (
                          <tr key={`${pref}-${cat}`}>
                            <td>
                              <span className="badge bg-light text-dark border">{cat}</span>
                            </td>
                            {earlyBirdEnabled && (
                              <td>
                                <div className="input-group input-group-sm">
                                  <span className="input-group-text bg-warning bg-soft text-warning">$</span>
                                  <Input
                                    type="number"
                                    name={earlyName}
                                    placeholder="0.00"
                                    value={getNestedValue(validation.values, earlyName) || ""}
                                    onChange={(e) => {
                                      const val = e.target.value
                                      if (val === "" || /^\d*\.?\d*$/.test(val)) {
                                        validation.setFieldValue(earlyName, val)
                                      }
                                    }}
                                    onBlur={validation.handleBlur}
                                    invalid={showEarlyError}
                                  />
                                </div>
                                {showEarlyError && <FormFeedback className="d-block">{earlyError}</FormFeedback>}
                              </td>
                            )}
                            <td>
                              <div className="input-group input-group-sm">
                                <span className="input-group-text bg-primary bg-soft text-primary">$</span>
                                <Input
                                  type="number"
                                  name={standardName}
                                  placeholder="0.00"
                                  value={getNestedValue(validation.values, standardName) || ""}
                                  onChange={(e) => {
                                    const val = e.target.value
                                    if (val === "" || /^\d*\.?\d*$/.test(val)) {
                                      validation.setFieldValue(standardName, val)
                                    }
                                  }}
                                  onBlur={validation.handleBlur}
                                  invalid={showStandardError}
                                />
                              </div>
                              {showStandardError && <FormFeedback className="d-block">{standardError}</FormFeedback>}
                            </td>
                          </tr>
                        )
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )
        })}
      </div>
    </>
  )
}

export default FoodPricing